import { LocationStrategy } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { Store, select } from '@ngrx/store';
import { MULTI_CART_FEATURE, MultiCartService, StateWithMultiCart } from '@spartacus/cart/base/core';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import {
  CmsService,
  GlobalMessageService,
  GlobalMessageType,
  LanguageService,
  Product,
  ProductService,
  RoutingService,
} from '@spartacus/core';
import { BREAKPOINT, BreakpointService, CmsComponentData, CurrentProductService } from '@spartacus/storefront';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subscription, combineLatest, concat } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { DealerPrice } from 'src/app/model/dealer-price.model';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { DealerPriceService } from 'src/app/service/dealer-price/dealer-price.service';
import {
  GoogleTagManagerEvent,
  GoogleTagManagerPrefix,
  GoogleTagManagerProductListName,
  GoogleTagManagerService,
} from 'src/app/service/googletagmanager/google-tag-manager.service';
import { CustomTranslationService } from 'src/app/service/language/custom-translation.service';
import { CustomProductService, ProductsByCategoryPayload } from 'src/app/service/product/custom-product.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CAROUSEL_ALTERNATIVE_PRODUCTS, TooltipIconService } from 'src/app/service/tooltip-icon/tooltip-icon.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { DealerPriceCheckerService } from 'src/app/utility/dealer-price-checker.service';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';
import { SlickCarouselUtilService } from 'src/app/utility/slick-carousel.util';
import { POINTER_EVENTS_NONE_CLASS } from '../../tooltip-icon/tooltip-icon.component';

@Component({
  selector: 'app-alternative-products',
  templateUrl: './alternative-products.component.html',
  styleUrls: ['./alternative-products.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AlternativeProductsComponent implements OnInit, OnDestroy {
  slickConfig: any;
  showSrp = false;
  srpSubscription$: Subscription;
  dealerPrices: DealerPrice[];
  cart: any;
  code: any;
  isMobile = false;
  isTablet = false;
  dealerPrices$: Observable<any>;
  csDesktopWidth = this.breakpointService.getSize(BREAKPOINT.lg);
  csTabletWidth = this.breakpointService.getSize(BREAKPOINT.sm);
  productCode: string;
  pCodes: any;
  itemsSubscription$: Subscription;
  hasDealerPrice = true;
  dealerPriceCheckerSubscription$: Subscription;
  atLeastOneHasHybrisPromo = false;
  atLeastOneHasErpPromo = false;

  isAlternative$: Observable<boolean> = this.componentData.data$.pipe(
    map((data) => data.uid.toUpperCase().includes('ALTERNATIVE'))
  );

  activeCountry$: Observable<string> = this.countryService.getActive();
  activeLanguage$: Observable<string> = this.languageService.getActive();

  noPointerEventsOnElement: string;
  tooltipIconConfig$: Observable<any> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.tooltipIconService.getTooltipIconConfig(activeCountry, CAROUSEL_ALTERNATIVE_PRODUCTS)
    ),
    tap((tooltipInfoConfig) => (this.noPointerEventsOnElement = tooltipInfoConfig.noPointerEventsOnElement))
  );
  showGstExclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_EXCLUSIVE)
    )
  );
  showGstInclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_INCLUSIVE)
    )
  );

  isCustomTranslationsEnabled$: Observable<boolean> = combineLatest([this.activeCountry$, this.activeLanguage$]).pipe(
    switchMap(([activeCountry, activeLanguage]) =>
      this.customTranslationService.isCustomTranslationsEnabled$(activeCountry, activeLanguage, [
        'productCarousel.alternativeProductTitle',
      ])
    )
  );
  businessType: string;
  gtmbusinessType: string = this.currentBusinesstype.getBusinessType();
  affiliation = `/${this.gtmbusinessType}/${this.gtmActiveCountry}/${this.activeLanguage}`;
  items$: Observable<any>;
  baseProdCategorySubscription$: Subscription;

  constructor(
    protected current: CurrentProductService,
    protected productService: ProductService,
    private customProductService: CustomProductService,
    private deviceService: DeviceDetectorService,
    private cartService: ActiveCartFacade,
    private cartMultiService: MultiCartService,
    private messageService: GlobalMessageService,
    private componentService: ComponentService,
    private dealerPriceService: DealerPriceService,
    private cdr: ChangeDetectorRef,
    private breakpointService: BreakpointService,
    private store: Store<StateWithMultiCart>,
    private gtmService: GoogleTagManagerService,
    private dealerPriceChecker: DealerPriceCheckerService,
    private countryService: ShimanoCountryService,
    private tooltipIconService: TooltipIconService,
    private routingService: RoutingService,
    private checkoutServiceExtends: CheckoutServiceExtends,
    private componentData: CmsComponentData<any>,
    private languageService: LanguageService,
    private customTranslationService: CustomTranslationService,
    private currentBusinesstype: CurrentBusinessType,
    private location: LocationStrategy,
    protected cms: CmsService,
    private storageService: StorageService
  ) {
    const slickConfig = new SlickCarouselUtilService();
    const slickObj = slickConfig.defaultConfig;
    slickObj.slidesToShow = 4;
    slickObj.slidesToScroll = 4;
    if (!this.deviceService.isDesktop()) {
      slickObj.arrows = false;
    }
    this.slickConfig = slickObj;
  }

  baseProdCategory$: Observable<any> = this.current.getProduct().pipe(
    filter(Boolean),
    map((p: any) => p),
    distinctUntilChanged()
  );

  private getAlternativeProducts(code: string, productCode: string): Observable<any> {
    const payload: ProductsByCategoryPayload = {
      baseProductId: code,
      fields: 'DEFAULT',
      restrictedProductCode: productCode,
    };
    this.productCode = productCode;
    if (typeof code !== 'undefined') {
      return this.customProductService.getProductsByCategory(payload).pipe(
        filter(Boolean),
        map((codes: any) => {
          if (codes.length > 0) {
            codes.forEach((pcode: any, index: any) => {
              if (pcode === this.productCode) {
                codes.splice(index, 1);
              }
            });
          }
          this.pCodes = codes;
          return codes;
        }),
        tap((productCodes: any) => {
          if (this.pCodes.length > 0) {
            this.getDealerPrices(productCodes);
          }
        }),
        map((data: any) => data.map((d: string) => this.productService.get(d)))
      );
    }
  }
  addToCart(code: string) {
    this.code = code;
    this.cartService
      .getEntry(code)
      .subscribe(() => {
        this.cartService.addEntry(code, 1);
        this.cart = this.store.pipe(select(MULTI_CART_FEATURE));
        const sub = this.cart.subscribe((cart: any) => {
          if (!cart.active.loading && !cart.active.error && cart.active.success) {
            this.showGlobalMessage('cart.addToCartSuccess', GlobalMessageType.MSG_TYPE_CONFIRMATION, this.code);
            sub.unsubscribe();
          }
          if (!cart.active.loading && cart.active.error && !cart.active.success) {
            sub.unsubscribe();
          }
        });
      })
      .unsubscribe();
  }
  private showGlobalMessage(text: string, param: string, messageType: GlobalMessageType) {
    this.messageService.add({ key: text, params: { productName: param } }, messageType);
    const element: HTMLElement = document.getElementsByClassName('BottomHeaderSlot')[0] as HTMLElement;
    element.scrollIntoView();
  }

  ngOnInit(): void {
    this.baseProdCategorySubscription$ = this.baseProdCategory$.subscribe((code) => {
      if (code) {
        this.items$ = this.getAlternativeProducts(code.baseProdCategory, code.code);
        this.cdr.detectChanges();
      }
    });
    this.isMobile = window.innerWidth < this.csTabletWidth;
    this.isTablet = window.innerWidth < this.csDesktopWidth && window.innerWidth >= this.csTabletWidth;
    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });
    this.itemsSubscription$ = this.items$
      .pipe(
        map((items$) => {
          combineLatest(items$.map((item: any) => concat(item))).subscribe((products: any) => {
            this.gtmAlternativeProductsImpressions(products, products.length);
            products.forEach((product: any) => {
              if (
                !this.atLeastOneHasHybrisPromo &&
                product &&
                product.potentialPromotions &&
                product.potentialPromotions[0].description
              )
                this.atLeastOneHasHybrisPromo = true;
              if (!this.atLeastOneHasErpPromo && product && this.erpPromoValue(product.code))
                this.atLeastOneHasErpPromo = true;
            });
          });
        })
      )
      .subscribe();

    this.dealerPriceCheckerSubscription$ = this.componentService.hasDealerPrice$.subscribe((hasDealerPrice) => {
      this.hasDealerPrice = hasDealerPrice;
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });
    this.businessType = this.currentBusinesstype.getBusinessType();
  }

  get gtmActiveCountry(): string {
    let activeCountry: string;
    this.countryService.getActive().subscribe((country) => (activeCountry = country));
    return activeCountry;
  }

  get activeLanguage(): string {
    let activeLanguage: string;
    this.languageService.getActive().subscribe((lang) => (activeLanguage = lang));
    return activeLanguage;
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  getDealerPrices(productCodes: string[]) {
    this.dealerPrices$ = this.dealerPriceService.getDealerPricesByProductCodes(productCodes);
    this.dealerPrices$.pipe(take(1), shareReplay({ bufferSize: 1, refCount: true })).subscribe((data) => {
      this.dealerPrices = data.dealerPrices;
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });
  }

  dealerPrice(productCode: string): string {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceByProductCode(this.dealerPrices, productCode);
    }
  }

  dealerPriceValue(productCode: string): number {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceValueByProductCode(this.dealerPrices, productCode);
    }
  }

  getImageRendition(image: any) {
    let size: string;
    if (this.isMobile || this.isTablet) {
      size = ShimanoUtilMethod.imageRendition(177);
    } else {
      size = ShimanoUtilMethod.imageRendition(240);
    }
    return ShimanoUtilMethod.newImageFormat(image, size);
  }

  goToPdp(product: Product, event: any, position: number) {
    if (!this.hasPointerEventsNone(event.target)) {
      this.routingService.go({
        cxRoute: 'product',
        params: product,
      });
      this.gtmAlternativeProductsClick(product, position);
    }
  }

  hasPointerEventsNone(element: Element): boolean {
    const closestElement = element.closest(this.noPointerEventsOnElement);
    return closestElement && closestElement.classList.contains(POINTER_EVENTS_NONE_CLASS);
  }

  dealerPriceData(productCode: string): DealerPrice {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceData(this.dealerPrices, productCode);
    }
  }

  erpPromoValue(productCode: string): string {
    if (this.dealerPrices) {
      return this.dealerPriceService.getErpPromoByProductCode(this.dealerPrices, productCode);
    }
  }

  get isDesktop$(): Observable<boolean> {
    return this.breakpointService.breakpoint$.pipe(map((breakpoint) => breakpoint >= BREAKPOINT.lg));
  }

  gtmAlternativeProductsImpressions(products: any[], count: number) {
    const impressionsList: any[] = [];
    products.forEach((product, index) => {
      if (product) {
        impressionsList.push({
          item_id: product.code,
          item_name: product.name,
          affiliation: this.affiliation,
          index: index,
          item_brand: product?.brand?.name,
          price: this.dealerPriceValue(product.code),
          item_list_name: GoogleTagManagerProductListName.ALTERNATIVE_PRODUCTS,
        });
      }
    });
    const gtmTag = {
      event: GoogleTagManagerEvent.PRODUCT_IMPRESSION,
      ecommerce: {
        currency: this.storageService.getSessionStorageItem('currency'),
        items: impressionsList,
      },
    };
    const gtmSessionStorageId =
      GoogleTagManagerPrefix + GoogleTagManagerProductListName.ALTERNATIVE_PRODUCTS.replace(/\s/g, '');
    if (!this.storageService.getSessionStorageItem(gtmSessionStorageId) && count === gtmTag.ecommerce.items.length) {
      this.storageService.setSessionStorageItem(gtmSessionStorageId, JSON.stringify(impressionsList));
      this.gtmService.pushTag({
        ecommerce: null,
        search_term: '',
        results_page: '',
        affiliation: '',
        content_type: '',
        content_id: '',
        content_name: '',
      });
      this.gtmService.pushTag(gtmTag);
    }
  }

  gtmAlternativeProductsClick(product: Product, position: number) {
    this.componentService.setResetPdpQty(1);
    const gtmTag = {
      event: GoogleTagManagerEvent.PRODUCT_CLICK,
      ecommerce: '',
      content_type: 'product',
      content_id: product.code,
      content_name: product.name,
    };
    this.gtmService.pushTag({
      ecommerce: null,
      search_term: '',
      results_page: '',
      affiliation: '',
      content_type: '',
      content_id: '',
      content_name: '',
    });
    this.gtmService.pushTag(gtmTag);
  }

  setCustomContext(isCustomTranslationsEnabled: boolean, activeCountry: string, activeLanguage: string) {
    return isCustomTranslationsEnabled ? [activeCountry, activeLanguage].join('-') : null;
  }

  ngOnDestroy() {
    if (this.srpSubscription$) {
      this.srpSubscription$.unsubscribe();
    }
    if (this.itemsSubscription$) {
      this.itemsSubscription$.unsubscribe();
    }
    if (this.dealerPriceCheckerSubscription$) {
      this.dealerPriceCheckerSubscription$.unsubscribe();
    }
    this.baseProdCategorySubscription$?.unsubscribe();
  }
}
